/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { CancelTokenSource } from 'axios';
import createTrack from 'services/actions/tracker_actions';
import {
  CampoArquivo,
  criarCancelamento,
  fmtBRL,
  foiCancelada,
  ImportarItensResponse,
  LinhaIgnorada,
  priceIntelligenceApi,
} from 'services/price_intelligence';
import {
  ehDocumento,
  formatarQuantidade,
  lerQuantidade,
  LOTE_MAX_ITENS,
  RascunhoItem,
  rascunhoDoArquivo,
  validarArquivo,
} from 'services/price_intelligence_batch';
import { Body } from './styles';

const ROTULO_CAMPO: Record<CampoArquivo, string> = {
  descricao: 'Descrição',
  quantidade: 'Quantidade',
  unidade: 'Unidade',
  numero_item: 'Nº do item',
  lote: 'Lote',
  codigo_catalogo: 'CATMAT/CATSER',
  valor_unitario: 'Valor de referência',
};

const DESCRICAO_MIN = 2;

type LinhaRevisao = RascunhoItem & {
  chave: string;
  incluir: boolean;
  qtdTexto: string;
  aba: string;
  linhaNoArquivo: number;
  origem: string;
  alerta: string | null;
};

const quantidadeInvalida = (texto: string) => texto.trim() !== '' && lerQuantidade(texto) === null;
const descricaoCurta = (texto: string) => texto.trim().length < DESCRICAO_MIN;

const mensagemDeErro = (err: any): string => {
  const erro = err && err.response && err.response.data && err.response.data.error;
  if (typeof erro === 'string' && erro.length < 300) return erro;
  return 'Não consegui ler o arquivo agora. Verifique a conexão e tente de novo.';
};

interface LinhaProps {
  linha: LinhaRevisao;
  onMudar: (chave: string, patch: Partial<LinhaRevisao>) => void;
  onDescartar: (chave: string) => void;
}

const LinhaDaRevisao = memo(({ linha: l, onMudar, onDescartar }: LinhaProps) => {
  const rotulo = l.numero || l.origem || l.descricao.slice(0, 40);
  const extras = [
    l.lote ? `Lote ${l.lote}` : null,
    l.codigoCatalogo ? `Catálogo ${l.codigoCatalogo}` : null,
    l.valorReferencia !== null ? `Ref. do arquivo ${fmtBRL(l.valorReferencia)}` : null,
    l.origem || null,
  ].filter(Boolean);
  const qtdInvalida = quantidadeInvalida(l.qtdTexto);
  const semDescricao = l.incluir && descricaoCurta(l.descricao);

  return (
    <tr className={l.incluir ? '' : 'excluded'}>
      <td>
        <input
          type="checkbox"
          checked={l.incluir}
          onChange={(e) => onMudar(l.chave, { incluir: e.target.checked })}
          aria-label={`Importar o item ${rotulo}`}
        />
      </td>
      <td className="muted">{l.numero || '—'}</td>
      <td>
        <textarea
          className={`form-control form-control-sm ${semDescricao ? 'is-invalid' : ''}`}
          value={l.descricao}
          onChange={(e) => onMudar(l.chave, { descricao: e.target.value })}
          rows={Math.min(5, Math.max(1, Math.ceil(l.descricao.length / 80)))}
          aria-label={`Descrição do item ${rotulo}`}
        />
        {extras.length > 0 && <p className="row-extra">{extras.join(' · ')}</p>}
        {l.alerta && (
          <p className="row-alert">
            <i className="material-icons">warning_amber</i>
            {l.alerta}
          </p>
        )}
      </td>
      <td>
        <input
          className={`form-control form-control-sm ${qtdInvalida ? 'is-invalid' : ''}`}
          value={l.qtdTexto}
          onChange={(e) => onMudar(l.chave, { qtdTexto: e.target.value })}
          inputMode="decimal"
          placeholder="—"
          aria-label={`Quantidade do item ${rotulo}`}
        />
        {qtdInvalida && <p className="row-alert">Não é um número: entra sem quantidade</p>}
      </td>
      <td>
        <input
          className="form-control form-control-sm text-uppercase"
          value={l.unidade || ''}
          onChange={(e) => onMudar(l.chave, { unidade: e.target.value })}
          placeholder="—"
          maxLength={20}
          aria-label={`Unidade do item ${rotulo}`}
        />
      </td>
      <td>
        <button
          type="button"
          className="icon-btn"
          onClick={() => onDescartar(l.chave)}
          title="Descartar este item"
          aria-label={`Descartar o item ${rotulo}`}
        >
          <i className="material-icons">delete_outline</i>
        </button>
      </td>
    </tr>
  );
});

interface PriceIntelligenceImportModalProps {
  itensNoLote: number;
  onClose: () => void;
  onImportar: (arquivo: string, itens: RascunhoItem[], modo: 'substituir' | 'adicionar') => void;
}

const PriceIntelligenceImportModal = ({
  itensNoLote,
  onClose,
  onImportar,
}: PriceIntelligenceImportModalProps) => {
  const [resposta, setResposta] = useState<ImportarItensResponse | null>(null);
  const [linhas, setLinhas] = useState<LinhaRevisao[]>([]);
  const [ignoradas, setIgnoradas] = useState<LinhaIgnorada[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState<boolean>(false);
  const [lendoDocumento, setLendoDocumento] = useState<boolean>(false);
  const [segundos, setSegundos] = useState<number>(0);
  const [arrastando, setArrastando] = useState<boolean>(false);
  const [confirmandoSubstituir, setConfirmandoSubstituir] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelRef = useRef<CancelTokenSource | null>(null);

  const emRevisao = Boolean(resposta) && !lendo;

  useEffect(
    () => () => {
      if (cancelRef.current) cancelRef.current.cancel();
    },
    [],
  );

  useEffect(() => {
    if (!lendo) return undefined;
    setSegundos(0);
    const relogio = window.setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => window.clearInterval(relogio);
  }, [lendo]);

  const recomecar = () => {
    if (cancelRef.current) cancelRef.current.cancel();
    setLendo(false);
    setResposta(null);
    setLinhas([]);
    setIgnoradas([]);
    setErro(null);
    setConfirmandoSubstituir(false);
  };

  const mudarLinha = useCallback((chave: string, patch: Partial<LinhaRevisao>) => {
    setLinhas((atual) => atual.map((l) => (l.chave === chave ? { ...l, ...patch } : l)));
  }, []);

  const descartarLinha = useCallback((chave: string) => {
    setLinhas((atual) => atual.filter((l) => l.chave !== chave));
  }, []);

  const enviar = async (arquivo: File | null | undefined) => {
    if (!arquivo) return;
    const invalido = validarArquivo(arquivo.name, arquivo.size);
    setErro(invalido);
    if (invalido) return;

    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = criarCancelamento();
    setLendoDocumento(ehDocumento(arquivo.name));
    setLendo(true);
    createTrack('Análise de Preços - Enviou arquivo de itens', {
      documento: ehDocumento(arquivo.name),
    });

    try {
      const data = await priceIntelligenceApi.importItems(arquivo, cancelRef.current);
      if (!data.itens || !data.itens.length) {
        setErro(data.mensagem || 'Não identifiquei itens neste arquivo.');
        return;
      }
      const variasAbas = data.abas.length > 1;
      const documento = data.tipo === 'documento';
      setResposta(data);
      setIgnoradas(data.ignoradas || []);
      setLinhas(
        data.itens.map((item, i) => {
          const rascunho = rascunhoDoArquivo(item);
          const origemPlanilha = variasAbas
            ? `${item.aba} · linha ${item.linha}`
            : `linha ${item.linha}`;
          return {
            ...rascunho,
            chave: `p-${i}`,
            incluir: true,
            qtdTexto: formatarQuantidade(rascunho.quantidade),
            aba: item.aba,
            linhaNoArquivo: item.linha,
            origem: documento ? (item.pagina ? `pág. ${item.pagina}` : '') : origemPlanilha,
            alerta: item.alerta || null,
          };
        }),
      );
    } catch (err) {
      if (foiCancelada(err)) return;
      setErro(mensagemDeErro(err));
    } finally {
      setLendo(false);
    }
  };

  const incluirIgnorada = (ignorada: LinhaIgnorada) => {
    setIgnoradas((atual) => atual.filter((i) => i !== ignorada));
    setLinhas((atual) => {
      const variasAbas = resposta ? resposta.abas.length > 1 : false;
      const nova: LinhaRevisao = {
        chave: `i-${ignorada.aba}-${ignorada.linha}`,
        incluir: true,
        qtdTexto: '',
        aba: ignorada.aba,
        linhaNoArquivo: ignorada.linha,
        origem: variasAbas
          ? `${ignorada.aba} · linha ${ignorada.linha}`
          : `linha ${ignorada.linha}`,
        alerta: null,
        numero: null,
        lote: null,
        descricao: ignorada.texto,
        quantidade: null,
        unidade: null,
        codigoCatalogo: null,
        valorReferencia: null,
      };
      let posicao = -1;
      atual.forEach((l, i) => {
        if (l.aba === nova.aba && l.linhaNoArquivo < nova.linhaNoArquivo) posicao = i;
      });
      const onde = posicao + 1;
      return atual.slice(0, onde).concat([nova], atual.slice(onde));
    });
  };

  const selecionadas = linhas.filter((l) => l.incluir);
  const documento = resposta && resposta.tipo === 'documento' ? resposta.documento : undefined;
  const comAlerta = linhas.filter((l) => l.alerta).length;
  const semDescricao = selecionadas.some((l) => descricaoCurta(l.descricao));
  const todasMarcadas = linhas.length > 0 && selecionadas.length === linhas.length;
  const vagasAoAdicionar = Math.max(0, LOTE_MAX_ITENS - itensNoLote);
  const podeImportar = selecionadas.length > 0 && !semDescricao;

  const confirmar = (modo: 'substituir' | 'adicionar') => {
    if (!resposta || !podeImportar) return;
    onImportar(
      resposta.arquivo,
      selecionadas.map((l) => ({
        numero: l.numero,
        lote: l.lote,
        descricao: l.descricao,
        quantidade:
          l.qtdTexto === formatarQuantidade(l.quantidade)
            ? l.quantidade
            : lerQuantidade(l.qtdTexto),
        unidade: (l.unidade || '').trim() || null,
        codigoCatalogo: l.codigoCatalogo,
        valorReferencia: l.valorReferencia,
      })),
      modo,
    );
  };

  const fecharSeLivre = () => {
    if (!emRevisao) onClose();
  };

  return (
    <Modal
      show
      onHide={fecharSeLivre}
      size={emRevisao ? 'xl' : undefined}
      centered
      backdrop={emRevisao ? 'static' : true}
    >
      <div className="modal-header">
        <div>
          <h5 className="modal-title">
            {emRevisao ? 'Confira os itens antes de importar' : 'Cotar itens de um arquivo'}
          </h5>
          <small className="text-muted">
            {emRevisao
              ? documento
                ? 'A IA copiou os itens do documento. Confira com o original e ajuste o que precisar — nada é pesquisado ainda.'
                : 'O texto é o da sua planilha, sem alteração. Ajuste o que precisar — nada é pesquisado ainda.'
              : 'Envie a planilha, o PDF ou o Word do edital e a IA identifica os itens. Depois você pesquisa todos de uma vez.'}
          </small>
        </div>
        <button aria-label="Fechar" className="close" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <Body
        className="modal-body"
        onDragOver={(e: React.DragEvent) => e.preventDefault()}
        onDrop={(e: React.DragEvent) => e.preventDefault()}
      >
        {!emRevisao && !lendo && (
          <>
            {erro && (
              <div className="alert alert-danger" role="alert">
                {erro}
              </div>
            )}
            <div
              className={`drop-zone ${arrastando ? 'dragging' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setArrastando(true);
              }}
              onDragLeave={() => setArrastando(false)}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setArrastando(false);
                enviar(e.dataTransfer.files && e.dataTransfer.files[0]);
              }}
            >
              <span className="drop-icon">
                <i className="material-icons">upload_file</i>
              </span>
              <p className="drop-title">Arraste o arquivo para cá</p>
              <p className="drop-sub">
                Planilha (.xlsx, .xls, .ods, .csv) até 5 MB · PDF ou Word (.docx, .odt) até 10 MB ·
                até {LOTE_MAX_ITENS} itens
              </p>
              <button
                type="button"
                className="btn btn-primary btn-ia"
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                <i className="material-icons">upload</i>
                Escolher arquivo
              </button>
              <input
                ref={inputRef}
                type="file"
                className="d-none"
                aria-label="Arquivo com os itens"
                onChange={(e) => {
                  enviar(e.target.files && e.target.files[0]);
                  e.target.value = '';
                }}
              />
            </div>
            <ul className="tips">
              <li>
                Planilha: uma linha por item, com uma coluna de descrição. Não precisa de modelo —
                títulos, capa e totais são ignorados.
              </li>
              <li>
                PDF ou Word (Termo de Referência, anexo do edital): a IA lê o texto e copia os
                itens. PDF digitalizado (foto ou escaneado) não é lido.
              </li>
              <li>
                Quantidade, unidade, nº do item e lote são aproveitados quando o arquivo tiver.
              </li>
              <li>Você confere e ajusta a lista antes de pesquisar.</li>
            </ul>
          </>
        )}

        {lendo && (
          <div className="reading" role="status">
            <span className="spinner-border text-success" aria-hidden="true" />
            <p className="reading-title">
              {lendoDocumento ? 'Lendo o documento…' : 'Lendo a planilha…'}
              {segundos >= 5 && <small> {segundos}s</small>}
            </p>
            <p className="reading-sub">
              {lendoDocumento
                ? 'A IA lê o texto e copia os itens, um a um. Lista curta sai em segundos; documento longo pode levar mais de um minuto.'
                : 'A IA localiza a aba, o cabeçalho e as colunas; os itens são copiados como estão na planilha.'}
            </p>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={recomecar}>
              Cancelar
            </button>
          </div>
        )}

        {emRevisao && resposta && (
          <>
            <div className="review-summary">
              <p>
                <i className="material-icons">{documento ? 'description' : 'table_view'}</i>
                <strong>
                  {resposta.total_itens}{' '}
                  {resposta.total_itens === 1 ? 'item encontrado' : 'itens encontrados'}
                </strong>{' '}
                em {resposta.arquivo}
              </p>
              {resposta.abas.map((aba) => (
                <div key={aba.nome} className="columns">
                  <span>
                    {aba.origem === 'ia' && <i className="material-icons">auto_awesome</i>}
                    Aba “{aba.nome}”{resposta.abas.length > 1 ? ` (${aba.itens})` : ''} —{' '}
                    {aba.origem === 'ia'
                      ? 'colunas identificadas pela IA:'
                      : 'colunas identificadas pelos títulos:'}
                  </span>
                  {aba.colunas.map((c) => (
                    <span
                      key={`${c.campo}-${c.coluna}`}
                      className="column-chip"
                      title={c.titulo ? `Título no arquivo: “${c.titulo}”` : 'Coluna sem título'}
                    >
                      {ROTULO_CAMPO[c.campo]} · col. {c.coluna}
                    </span>
                  ))}
                </div>
              ))}
            </div>

            {resposta.truncado && (
              <div className="alert alert-warning">
                O arquivo tem mais itens do que o limite de {resposta.limite_itens} por importação:
                só os primeiros {resposta.total_itens} entraram. Divida o arquivo para importar o
                restante.
              </div>
            )}
            {resposta.leitura_incompleta && (
              <div className="alert alert-warning">
                {documento &&
                documento.paginas &&
                documento.paginas_lidas &&
                documento.paginas_lidas < documento.paginas
                  ? `O documento tem ${documento.paginas} páginas e só as ${documento.paginas_lidas} primeiras foram lidas. Se a lista de itens está mais adiante, envie só as páginas dela.`
                  : 'O arquivo foi lido só em parte: pode ter faltado item. Confira com o original; se faltou, envie só a lista de itens e importe de novo.'}
              </div>
            )}
            {documento && documento.lacunas && documento.lacunas.length > 0 && (
              <div className="alert alert-warning">
                A numeração dos itens pula{' '}
                {documento.lacunas.map(([de, ate]) => `do ${de} para o ${ate}`).join(', ')}: pode
                ter faltado item. Confira com o documento.
              </div>
            )}
            {documento && documento.paginas_sem_texto > 0 && (
              <div className="alert alert-warning">
                {documento.paginas_sem_texto === 1
                  ? '1 página do PDF é imagem (digitalizada) e não foi lida.'
                  : `${documento.paginas_sem_texto} páginas do PDF são imagem (digitalizadas) e não foram lidas.`}{' '}
                Se a lista de itens está nelas, envie o arquivo original.
              </div>
            )}
            {comAlerta > 0 && (
              <div className="alert alert-warning">
                {comAlerta === 1
                  ? '1 item tem descrição ou quantidade que não localizei no documento'
                  : `${comAlerta} itens têm descrição ou quantidade que não localizei no documento`}
                : confira os marcados antes de importar.
              </div>
            )}
            {documento && documento.repetidos > 0 && (
              <p className="muted small-note">
                {documento.repetidos === 1
                  ? '1 item repetido ficou de fora'
                  : `${documento.repetidos} itens repetidos ficaram de fora`}{' '}
                (o documento traz a mesma lista mais de uma vez).
              </p>
            )}

            {ignoradas.length > 0 && (
              <details className="ignored">
                <summary>
                  {resposta.total_ignoradas > ignoradas.length
                    ? `${ignoradas.length} de ${resposta.total_ignoradas} linhas que ficaram de fora`
                    : `${ignoradas.length} ${
                        ignoradas.length === 1 ? 'linha ficou' : 'linhas ficaram'
                      } de fora`}{' '}
                  (títulos, totais) — conferir
                </summary>
                <ul>
                  {ignoradas.map((ig) => (
                    <li key={`${ig.aba}-${ig.linha}-${ig.tipo}`}>
                      <span>
                        <span className="muted">
                          Linha {ig.linha} · {ig.motivo}:
                        </span>{' '}
                        {ig.texto}
                      </span>
                      {ig.tipo === 'titulo' && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => incluirIgnorada(ig)}
                        >
                          É um item
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="review-table">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={todasMarcadas}
                        onChange={(e) =>
                          setLinhas((atual) =>
                            atual.map((l) => ({ ...l, incluir: e.target.checked })),
                          )
                        }
                        aria-label="Selecionar todos os itens"
                      />
                    </th>
                    <th style={{ width: 56 }}>Nº</th>
                    <th>Descrição</th>
                    <th style={{ width: 120 }}>Quantidade</th>
                    <th style={{ width: 100 }}>Unidade</th>
                    <th style={{ width: 40 }} aria-label="Descartar" />
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <LinhaDaRevisao
                      key={l.chave}
                      linha={l}
                      onMudar={mudarLinha}
                      onDescartar={descartarLinha}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {itensNoLote > 0 && selecionadas.length > vagasAoAdicionar && (
              <p className="small-note warn">
                A lista atual tem {itensNoLote} itens e comporta {LOTE_MAX_ITENS}: ao adicionar, só{' '}
                {vagasAoAdicionar === 1
                  ? 'cabe mais 1 item'
                  : `cabem mais ${vagasAoAdicionar} itens`}
                .
              </p>
            )}
          </>
        )}
      </Body>

      {emRevisao && (
        <div className="modal-footer justify-content-between">
          <small className={semDescricao ? 'text-danger' : 'text-muted'}>
            {semDescricao
              ? 'Há item selecionado sem descrição — preencha ou desmarque.'
              : `${selecionadas.length} de ${linhas.length} selecionados`}
          </small>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" className="btn btn-outline-secondary" onClick={recomecar}>
              Trocar arquivo
            </button>
            {itensNoLote > 0 ? (
              <>
                <button
                  type="button"
                  className={`btn ${
                    confirmandoSubstituir ? 'btn-outline-danger' : 'btn-outline-secondary'
                  }`}
                  disabled={!podeImportar}
                  onClick={() =>
                    confirmandoSubstituir ? confirmar('substituir') : setConfirmandoSubstituir(true)
                  }
                  onBlur={() => setConfirmandoSubstituir(false)}
                >
                  {confirmandoSubstituir
                    ? `Confirmar: descartar os ${itensNoLote} itens atuais`
                    : 'Substituir a lista atual'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-ia"
                  disabled={!podeImportar || vagasAoAdicionar === 0}
                  onClick={() => confirmar('adicionar')}
                >
                  Adicionar à lista atual
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-ia"
                disabled={!podeImportar}
                onClick={() => confirmar('substituir')}
              >
                Importar {selecionadas.length} {selecionadas.length === 1 ? 'item' : 'itens'}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PriceIntelligenceImportModal;
